import { Bell } from "lucide-react";

import { useNotificationPermission } from "./useNotificationPermission";

// A prominent, non-blocking banner so a signed-in user with care reminders on (the default)
// doesn't have to discover Settings first to enable device delivery. It is shown only in an
// installed standalone PWA (see showEnablePrompt) -- browser use stays first-class and is never
// nagged here; Settings is the management surface for it. Positioned at the top of the viewport
// (PwaStatusPrompts already owns the bottom) so the two can never visually collide if both happen
// to be eligible at once -- deliberately no cross-component coordination between them, since they
// are unrelated prompts (installing the app vs. enabling notifications).
//
// Not modal, not blocking, and never an error surface. One pre-prompt action only: "Enable
// notifications" -- MedoPilot requires notifications for core features, so there is deliberately no
// "Not now" / close control to voluntarily dismiss the invitation without deciding. It still
// disappears on its own once the device is enabled (state leaves "not-enabled"), once permission
// is blocked/denied (state moves to "blocked"), and the moment an enable() attempt fails after
// permission was granted -- a failed setup must never sit over Home. When it goes away for one of
// those reasons, the classified status and retry live in Settings > Notifications.
//
// This never calls Notification.requestPermission() itself -- "Enable notifications" only invokes
// the same explicit, user-gesture-gated enable() flow the Settings card already uses.
export function NotificationEnablePrompt() {
  const { enabling, enable, showEnablePrompt } = useNotificationPermission();

  if (!showEnablePrompt) {
    return null;
  }

  return (
    <aside
      className="fixed inset-x-4 top-[calc(1.25rem+env(safe-area-inset-top))] z-50 mx-auto max-w-md rounded-2xl border border-primary/15 bg-white p-4 shadow-premium sm:max-w-lg"
      aria-live="polite"
      aria-label="Enable MedoPilot notifications"
    >
      <div className="flex items-start gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
          <Bell className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Enable MedoPilot notifications</p>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            Care reminders are on. Enable notifications on this device so MedoPilot can deliver them here.
          </p>
          <div className="mt-3">
            <button
              type="button"
              onClick={() => void enable()}
              disabled={enabling}
              className="inline-flex min-h-9 items-center justify-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {enabling ? "Enabling…" : "Enable notifications"}
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}
